import { useStore } from '@/providers/app-store-provider';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { isInZeloolApp, isMobileScreenFunc } from '@/utils/tools';
import { useEffect, useLayoutEffect, useRef } from 'react';

const HOME_HEADER_STATE_KEY = '__zeeloolHomeHeaderCollapsed';

function readHomeHeaderState(state: unknown): boolean | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const value = (state as Record<string, unknown>)[HOME_HEADER_STATE_KEY];
  return typeof value === 'boolean' ? value : undefined;
}

function rememberHomeHeaderState(collapsed: boolean) {
  const state: unknown = window.history.state;
  if (readHomeHeaderState(state) === collapsed) return;
  // 保留 Next.js 路由树等字段，仅更新当前历史条目的首页展示状态。
  window.history.replaceState(
    {
      ...(typeof state === 'object' && state !== null ? state : {}),
      [HOME_HEADER_STATE_KEY]: collapsed,
    },
    '',
  );
}

export const useEventListenInit = () => {
  const { pathname } = useRouteMetadata();
  const previousPathname = useRef(pathname);
  const isHistoryNavigation = useRef(false);
  const isHomeScrollRestoring = useRef(false);
  const scrollIconTimer = useRef<NodeJS.Timeout | null>(null);
  const isMobileScreenRef = useRef<boolean>(false);
  const setIsShowAppDownloadBanner = useStore((state) => state.setIsShowAppDownloadBanner);
  const setIsShowDtBanner = useStore((state) => state.setIsShowDtBanner);
  const setIsHomeHeaderCollapsed = useStore((state) => state.setIsHomeHeaderCollapsed);
  const setIsShowScrollToTop = useStore((state) => state.setIsShowScrollToTop);
  const setIsHalfStickyRightBottomIcons = useStore(
    (state) => state.setIsHalfStickyRightBottomIcons,
  );
  const setIsMobileScreen = useStore((state) => state.setIsMobileScreen);

  useLayoutEffect(() => {
    // 新导航回首页从顶部展开；浏览器后退沿用该首页离开前的状态。
    if (pathname === '/' && !isHistoryNavigation.current) {
      setIsHomeHeaderCollapsed(false);
      rememberHomeHeaderState(false);
    }
    if (pathname !== '/' || !isHistoryNavigation.current) {
      isHomeScrollRestoring.current = false;
    }
    previousPathname.current = pathname;
    isHistoryNavigation.current = false;
  }, [pathname, setIsHomeHeaderCollapsed]);

  useEffect(() => {
    // 判断是否为移动端（以680px为分界点）
    const isMobileSc = isMobileScreenFunc();

    // 全局存储
    isMobileScreenRef.current = isMobileSc;
    setIsMobileScreen(isMobileSc);

    const handleScroll = () => {
      const { scrollTop } = document.querySelector('html');
      // popstate 先更新地址，滚动恢复和 React 提交可能随后才完成。
      // 目标页遗留的 scrollTop=0 不能覆盖首页离开前的横幅状态。
      const isCommittedHome = location.pathname === '/' && previousPathname.current === '/';
      if (isCommittedHome && scrollTop > 0) {
        isHomeScrollRestoring.current = false;
      }
      // 处理顶通和APP下载的隐藏和显示
      // APP下载Banner只会在首页展示，其他页面不展示
      // 这里其实是靠配置的，目前只在首页展示，这样写也还行，后续如果扩展，还是需要改掉！！
      if (isCommittedHome && !isHomeScrollRestoring.current) {
        if (scrollTop === 0) {
          setIsHomeHeaderCollapsed(false);
          rememberHomeHeaderState(false);
          setIsShowAppDownloadBanner(true);
        }
        if (scrollTop >= 125) {
          setIsHomeHeaderCollapsed(true);
          rememberHomeHeaderState(true);
          setIsShowAppDownloadBanner(false);
        }
      } else if (location.pathname !== '/') {
        setIsShowAppDownloadBanner(false);
      }
      if (location.pathname === '/') {
        if (scrollTop === 0) {
          setIsShowDtBanner(true);
        }
        if (scrollTop >= 125) {
          setIsShowDtBanner(false);
        }
        // TODO: 这里还有待做的，就是前页面已经隐藏了，那么下一个页面还是隐藏，除非用户滚动起来！
      } else if (scrollTop === 0) {
        setIsShowDtBanner(true);
      } else {
        setIsShowDtBanner(false);
      }
      // 处理侧标的右下角的图标的展示和半隐藏
      // 这里的53是一个大致的高度，顶部header高度为53，其他带有顶部的页面与之大致相同
      const onePageHeight = window.innerHeight - 53;
      if (scrollTop > onePageHeight) {
        setIsShowScrollToTop(true);
      } else {
        setIsShowScrollToTop(false);
      }
      setIsHalfStickyRightBottomIcons(true);
      if (scrollIconTimer.current) {
        clearTimeout(scrollIconTimer.current);
      }
      scrollIconTimer.current = setTimeout(() => {
        setIsHalfStickyRightBottomIcons(false);
      }, 2000);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'zl-token') {
        window.location.reload();
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      isHistoryNavigation.current = location.pathname !== previousPathname.current;
      isHomeScrollRestoring.current = isHistoryNavigation.current && location.pathname === '/';
      if (location.pathname === '/') {
        const collapsed = readHomeHeaderState(event.state);
        if (collapsed !== undefined) setIsHomeHeaderCollapsed(collapsed);
      }
    };

    // 用户开始主动滚动后，恢复正常的顶部展开行为，不用定时器猜恢复耗时。
    const handleScrollIntent = () => {
      isHomeScrollRestoring.current = false;
    };

    // 监听窗口尺寸变化
    const handleResize = () => {
      const isMobileS = window.innerWidth < 680;

      // 如果设备类型发生变化，刷新页面
      if (isMobileScreenRef.current !== isMobileS) {
        // 使用window.location.reload()刷新页面
        window.location.reload();
        setIsMobileScreen(isMobileS);
        isMobileScreenRef.current = isMobileS;
      }
    };

    // 添加事件监听器
    window.addEventListener('scroll', handleScroll, { passive: false });
    window.addEventListener('resize', handleResize);
    window.addEventListener('popstate', handlePopState, true);
    window.addEventListener('wheel', handleScrollIntent, { passive: true });
    window.addEventListener('touchstart', handleScrollIntent, { passive: true });
    window.addEventListener('keydown', handleScrollIntent);

    if (!isInZeloolApp()) {
      // 另一个tab页登录状态发生变化，刷新页面
      window.addEventListener('storage', handleStorage);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('popstate', handlePopState, true);
      window.removeEventListener('wheel', handleScrollIntent);
      window.removeEventListener('touchstart', handleScrollIntent);
      window.removeEventListener('keydown', handleScrollIntent);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);
};
